import React from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import noImage from "../../images/no-image-placeholder.png";
import LakeViewIcon from "../../images/lake-view-icon.svg";
import AgentProfileCard from "../Common/AgentProfileCard";
import {
  thousandSeperatedValue,
  removeDecimalZeros,
} from "../../helpers/common";

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
};

const STATUS_LABEL = {
  Active: "Active",
  ActiveUnderContract: "Under Contract",
  Pending: "Pending",
};

const STATUS_VARIANT = {
  Active: "status-pill--active",
  ActiveUnderContract: "status-pill--contract",
  Pending: "status-pill--pending",
};

const formatAddress = (l = {}) => {
  const cityStateZip = [
    [l.City, l.State].filter(Boolean).join(", "),
    l.ZIP,
  ]
    .filter(Boolean)
    .join(" ");
  return [l.Location, cityStateZip].filter(Boolean).join(" • ");
};

const formatPrice = (p) => {
  if (p === null || p === undefined || p === "") return null;
  const n = parseFloat(p);
  if (Number.isNaN(n) || n <= 0) return null;
  return "$" + thousandSeperatedValue(removeDecimalZeros(n));
};

const MatchScoreBadge = ({ score }) => {
  if (score === null || score === undefined || score === "") return null;
  const n = Math.round(parseFloat(score));
  if (Number.isNaN(n) || n <= 0) return null;
  return (
    <span className="mls-match-score">
      <span className="mls-match-score__pct">{n}%</span>
      <span className="mls-match-score__label">Match</span>
    </span>
  );
};

const MlsMatchCard = ({ list, onClick }) => {
  const navigate = useNavigate();
  const listingKey =
    _.get(list, "mlsListingKey") || _.get(list, "ListingKey");
  const photo = _.get(list, "photo");
  const status = _.get(list, "StandardStatus", "");
  const beds = _.get(list, "Bedrooms");
  const baths = _.get(list, "Bathrooms");
  const sqft = _.get(list, "Sqft");
  const N2H = _.get(list, "N2H") || [];
  const price = formatPrice(_.get(list, "AskingPrice_Sell"));
  const score = _.get(list, "score");
  const listedDate = formatDate(_.get(list, "PublishedOn"));
  const agentFirstName = _.get(list, "userFirstName");
  const agentLastName = _.get(list, "userLastName");
  const agentCompany = _.get(list, "userCompany");
  const hasAgent = Boolean(agentFirstName || agentLastName);

  const handleClick = () => {
    sessionStorage.setItem("wantAdMatchesScrollY", String(window.scrollY));
    if (onClick) return onClick(listingKey, list);
    if (listingKey) navigate(`/mls-listing/${encodeURIComponent(listingKey)}`);
  };

  const statusLabel = STATUS_LABEL[status] || status;
  const statusVariant = STATUS_VARIANT[status] || "";

  return (
    <div
      className="mls-match-card"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="mls-match-card__media">
        <img
          src={photo || noImage}
          alt=""
          onError={(e) => {
            e.currentTarget.src = noImage;
          }}
        />
        <span className="mls-match-card__badge">MLS</span>
        {statusLabel && (
          <span className={`status-pill ${statusVariant}`.trim()}>
            {statusLabel}
          </span>
        )}
      </div>
      <div className="mls-match-card__body">
        {listedDate && (
          <div className="mls-match-card__listed-date">
            Listed {listedDate}
          </div>
        )}
        <div className="mls-match-card__price-row">
          {price && <div className="mls-match-card__price">{price}</div>}
          <MatchScoreBadge score={score} />
        </div>
        <div className="mls-match-card__address">{formatAddress(list)}</div>
        <div className="mls-match-card__facts">
          {beds != null && <span>{beds} bd</span>}
          {baths != null && <span>{baths} ba</span>}
          {sqft != null && <span>{thousandSeperatedValue(sqft)} sqft</span>}
        </div>
        {N2H?.length > 0 && (
          <div className="mls-match-card__chips">
            {N2H.slice(0, 8).map((n, i) => {
              const text = _.get(n, "Text", "");
              const icon = _.get(n, "Icon", "");
              return (
                <span key={i} className="mls-match-card__chip">
                  <img
                    src={
                      icon ? require("../../images/" + icon) : LakeViewIcon
                    }
                    alt=""
                  />
                  <span>{text}</span>
                </span>
              );
            })}
          </div>
        )}
        {hasAgent && (
          <div className="mls-match-card__agent property-agent-info">
            <AgentProfileCard
              isAgentCreated
              userFirstName={agentFirstName}
              userLastName={agentLastName}
              userCompany={agentCompany}
              showAvatar={false}
              showCompany={Boolean(agentCompany)}
              showProBadge={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MlsMatchCard;
